import React, { useState } from 'react';
import { Sidebar } from './Sidebar';
import { LayoutDashboard, FileText, School, Calendar, Users, Trophy, Key, Settings, ClipboardList } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import schoolLogo from '../../assets/school-logo-header.png';

interface AdminLayoutProps {
    children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { logout, user, adminRole } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/admin/login');
        } catch (error) {
            console.error('Failed to logout', error);
        }
    };

    const allMenuItems = [
        { name: 'Dashboard', path: '/admin/dashboard', icon: <LayoutDashboard size={20} />, roles: ['supreme_admin', 'admin', 'event_manager', 'escort'] },
        { name: 'Logs', path: '/admin/logs', icon: <FileText size={20} />, roles: ['supreme_admin', 'admin'] },
        { name: 'Schools', path: '/admin/schools', icon: <School size={20} />, roles: ['supreme_admin', 'admin'] },
        { name: 'Events', path: '/admin/events', icon: <Calendar size={20} />, roles: ['supreme_admin', 'admin', 'event_manager'] },
        { name: 'Registrations', path: '/admin/registrations', icon: <ClipboardList size={20} />, roles: ['supreme_admin', 'admin', 'event_manager'] },
        { name: 'Participants', path: '/admin/participants', icon: <Users size={20} />, roles: ['supreme_admin', 'admin', 'event_manager'] },
        { name: 'Results', path: '/admin/results', icon: <Trophy size={20} />, roles: ['supreme_admin', 'admin', 'event_manager'] },
        { name: 'Access Keys', path: '/admin/keys', icon: <Key size={20} />, roles: ['supreme_admin', 'admin'] },
        { name: 'Admins', path: '/admin/admins', icon: <Settings size={20} />, roles: ['supreme_admin'] },
    ];

    const menuItems = allMenuItems.filter(item => item.roles.includes(adminRole || 'admin'));

    return (
        <div className="min-h-screen bg-deep-black text-white relative overflow-hidden">
            {/* Layered Background Effect */}
            <div className="fixed inset-0 pointer-events-none">
                {/* Subtle brand gradient at top */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,153,28,0.03)_0%,transparent_50%)]" />
                {/* Grid pattern */}
                <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.02]" />
            </div>

            {/* Mobile Header */}
            <div className="md:hidden h-16 bg-deep-black/90 backdrop-blur-xl border-b border-white/[0.04] flex items-center justify-between px-4 sticky top-0 z-50">
                <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg overflow-hidden shrink-0">
                        <img src={schoolLogo} alt="Logo" className="w-full h-full object-contain" />
                    </div>
                    <span className="font-display font-bold text-white text-sm tracking-[0.15em]">ENIGMA</span>
                </div>
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="w-10 h-10 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.06] transition-all duration-200"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {isMobileMenuOpen ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        )}
                    </svg>
                </button>
            </div>

            {/* Desktop Sidebar */}
            <div className="hidden md:block">
                <Sidebar
                    items={menuItems}
                    isOpen={isSidebarOpen}
                    onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
                    onLogout={handleLogout}
                    title="Admin Console"
                    userEmail={user?.email || 'Admin'}
                    userName={user?.name || 'Admin User'}
                />
            </div>

            {/* Mobile Drawer with Backdrop */}
            {isMobileMenuOpen && (
                <div className="md:hidden fixed inset-0 z-40">
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                    <Sidebar
                        items={menuItems}
                        isOpen={isMobileMenuOpen}
                        onToggle={() => setIsMobileMenuOpen(false)}
                        onLogout={handleLogout}
                        title="Admin Console"
                        userEmail={user?.email || 'Admin'}
                        userName={user?.name || 'Admin User'}
                        mobile={true}
                    />
                </div>
            )}

            {/* Main Content Area */}
            <main
                className={`
                    min-h-screen relative z-10
                    transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]
                    ml-0
                    ${isSidebarOpen ? 'md:ml-[260px]' : 'md:ml-[72px]'}
                `}
            >
                <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
                    <div className="animate-fade-in">
                        {children}
                    </div>
                </div>
            </main>
        </div>
    );
};
